/**
 * Event Registrations API Service
 * Handles webinar and event registration API calls
 */

import axios from 'axios';
import { apiClient } from '../apiClient';

import { config as productConfig } from '@/src/config';
const API_BASE = import.meta.env.MODE === 'production' ? '' : (productConfig.weaveinApi.baseUrl ?? '').trim();

export interface EventRegistrationRequest {
  full_name: string;
  email: string;
  organization_name?: string;
  phone_number: string;
  event_id: string;
  event_title: string;
  event_date: string;
}

export interface EventRegistrationResponse {
  success: boolean;
  message: string;
  registration_id: number;
  organization_id: number;
  email: string;
  zoom_link?: string;
}

export interface UpcomingEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  description: string;
  event_date?: string;
  event_time?: string;
  timezone?: string;
  duration_minutes?: number;
  is_featured?: boolean;
  max_attendees?: number;
  registration_count?: number;
  spots_available?: number;
}

export interface EventRegistration {
  id: number;
  full_name: string;
  email: string;
  organization_name: string;
  phone_number: string;
  registered_at: string;
  event_id?: string;
}

export interface EventRegistrationsResponse {
  event_id: string;
  count: number;
  registrations: EventRegistration[];
}

export interface CalendarLinks {
  google: string;
  outlook: string;
  ics_content: string;
  start_datetime: string;
  end_datetime: string;
}

export interface CalendarLinksResponse {
  event_id: string;
  title: string;
  calendar_links: CalendarLinks;
  zoom_link?: string;
}

export interface EventDetails extends UpcomingEvent {
  event_id: string;
  status: string;
  tags?: string[];
  speakers?: {
    name: string;
    title?: string;
    organization?: string;
    bio?: string;
    photo_url?: string;
    linkedin_url?: string;
  }[];
  resources?: {
    title: string;
    type: string;
    url?: string;
  }[];
}

export interface NewsletterSubscriptionResponse {
  success: boolean;
  message: string;
  email: string;
  already_subscribed: boolean;
}

/**
 * Register for an event/webinar (public)
 */
export async function registerForEvent(data: EventRegistrationRequest): Promise<EventRegistrationResponse> {
  const response = await axios.post<EventRegistrationResponse>(
    `${API_BASE}/api/events/register`,
    data
  );
  return response.data;
}

/**
 * Get list of upcoming events (public)
 */
export async function getUpcomingEvents(): Promise<UpcomingEvent[]> {
  const response = await axios.get<{ events: UpcomingEvent[] }>(
    `${API_BASE}/api/events/events`
  );
  return response.data.events;
}

/**
 * Get event details by event_id (public)
 */
export async function getEventDetails(eventId: string): Promise<EventDetails> {
  const response = await axios.get<EventDetails>(
    `${API_BASE}/api/events/events/${eventId}`
  );
  return response.data;
}

/**
 * Get calendar links for an event (public)
 */
export async function getCalendarLinks(eventId: string): Promise<CalendarLinksResponse> {
  const response = await axios.get<CalendarLinksResponse>(
    `${API_BASE}/api/events/events/${eventId}/calendar`
  );
  return response.data;
}

/**
 * Subscribe to the newsletter (public)
 */
export async function subscribeToNewsletter(email: string): Promise<NewsletterSubscriptionResponse> {
  const response = await axios.post<NewsletterSubscriptionResponse>(
    `${API_BASE}/api/events/newsletter/subscribe`,
    { email }
  );
  return response.data;
}

// ============================================================================
// ADMIN ENDPOINTS (require authentication)
// ============================================================================

/**
 * Get all registrations for an event (admin)
 */
export async function getEventRegistrations(eventId: string): Promise<EventRegistrationsResponse> {
  const response = await apiClient.get<EventRegistrationsResponse>(
    `/api/events/registrations/${eventId}`
  );
  return response.data;
}

/**
 * Delete a registration by ID (admin)
 */
export async function deleteRegistration(registrationId: number): Promise<{
  success: boolean;
  message: string;
  deleted_id: number;
  event_id: string;
}> {
  const response = await apiClient.delete(`/api/events/registrations/${registrationId}`);
  return response.data;
}

/**
 * Delete a registration by email and event_id (admin)
 */
export async function deleteRegistrationByEmail(eventId: string, email: string): Promise<{
  success: boolean;
  message: string;
  deleted_id: number;
  event_id: string;
}> {
  const response = await apiClient.delete(
    `/api/events/registrations/by-email/${eventId}`,
    { params: { email } }
  );
  return response.data;
}
